/**
 * Map category titles → lucide-react icon components so the home strip and
 * detail-page chips have meaningful artwork without storing icons in the
 * database. Falls back to a generic Tag for anything we don't know.
 *
 * Keys are matched case-insensitively against the category title; substring
 * matches are used so "Health & Beauty" matches the "beauty" key.
 *
 * Usage:
 *   const Icon = iconForCategory(category.title);
 *   <Icon size={16} />
 */
import {
  Baby,
  BookOpen,
  Box,
  Camera,
  Candy,
  Car,
  Coffee,
  Croissant,
  Dumbbell,
  Egg,
  Fish,
  Gamepad2,
  HeartPulse,
  Home,
  Laptop,
  Leaf,
  Monitor,
  PawPrint,
  Plane,
  Scissors,
  Shirt,
  ShoppingBasket,
  Smartphone,
  Snowflake,
  Sparkles,
  Tag,
  Tent,
  Tv,
  UtensilsCrossed,
  type LucideIcon,
} from 'lucide-react';

// Order matters — first match wins. Be specific before generic.
const ICON_MAP: Array<[RegExp, LucideIcon]> = [
  // Groceries & food
  [/groceries|supermarket|food/i, ShoppingBasket],
  [/produce|fruit|vegetable/i, Leaf],
  [/meat|seafood/i, Fish],
  [/dairy|egg/i, Egg],
  [/bakery|bread/i, Croissant],
  [/beverage|drink/i, Coffee],
  [/frozen/i, Snowflake],
  [/snack|confection|candy/i, Candy],

  // Electronics & tech
  [/electronic|tech/i, Laptop],
  [/phone|mobile/i, Smartphone],
  [/computer|laptop/i, Monitor],
  [/tv|television/i, Tv],
  [/camera|photo/i, Camera],
  [/gaming|game/i, Gamepad2],

  // Home
  [/home appliance|appliance/i, Box],
  [/home|furniture/i, Home],
  [/kitchen/i, UtensilsCrossed],

  // Fashion / clothing
  [/fashion|apparel|clothing|shoe|bag|accessor/i, Shirt],

  // Beauty / health
  [/beauty|cosmet|makeup/i, Sparkles],
  [/health|pharma|medic/i, HeartPulse],

  // Sports & outdoors
  [/sport|fitness|gym/i, Dumbbell],
  [/outdoor|camping/i, Tent],

  // Kids & toys
  [/baby|kid|toy/i, Baby],

  // Books & media
  [/book|media|stationery/i, BookOpen],

  // Pets
  [/pet|animal/i, PawPrint],

  // Auto
  [/auto|car|vehicle/i, Car],

  // Travel
  [/travel|tour|hotel|flight/i, Plane],

  // Services
  [/service|salon|spa/i, Scissors],
];

export function iconForCategory(title?: string | null): LucideIcon {
  if (!title) return Tag;
  for (const [re, icon] of ICON_MAP) {
    if (re.test(title)) return icon;
  }
  return Tag;
}
